<template>
  <div class="mb-3 border-bottom pb-2">
    <label class="fw-semibold">
      {{ questionNumber }}. {{ question.name }}
    </label>
    <br />
    <div class="mt-1">
      <!-- Conditionally render radio buttons for SocityBool -->
      <div
        v-if="question.type === 'SocietyBool' || !question.type"
        class="mb-2 d-flex gap-3"
      >
        <label class="form-check-label">
          <input
            type="radio"
            class="form-check-input me-1"
            :name="radioGroupName"
            v-model="localIsTrue"
            :value="true"
            required
            :disabled="disabled"
          />
          Sí
        </label>
        <label class="form-check-label">
          <input
            type="radio"
            class="form-check-input me-1"
            :name="radioGroupName"
            v-model="localIsTrue"
            :value="false"
            required
            :disabled="disabled"
          />
          No
        </label>
      </div>

      <b-collapse
        :id="'details-collapse-' + path.join('-')"
        :visible="localIsTrue"
      >
        <div class="mt-2 mb-2 ps-3">
          <ListEdit
            :value="modelValue.details"
            label="Detalles"
            :minItems="1"
            @handleAdd="handleAddDetail"
            @handleRemove="handleRemoveDetail"
          >
            <template #header>
              <div class="row gx-2 text-muted small">
                <div class="col">Nombre</div>
                <div class="col">RUT</div>
                <div class="col">
                  {{ question.fieldNameCargo || "Rol" }}
                </div>
                <div class="col">{{ question.fieldNameDate || "Periodo" }}</div>
              </div>
            </template>
            <template #default="{ index, item }">
              <div class="row gx-2 mb-1 align-items-center">
                <div class="col">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="item.name"
                    :placeholder="'Nombre ' + (index + 1)"
                    :disabled="disabled"
                    required
                  />
                </div>
                <div class="col">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    :value="rutDisplayValue(item)"
                    @input="handleInputRUT($event, item)"
                    @focus="handleFocusRUT(item)"
                    @blur="handleBlurRUT(item)"
                    @keypress="validateRutMethod"
                    :placeholder="'RUT ' + (index + 1)"
                    :maxlength="9"
                    inputmode="numeric"
                    required
                    :disabled="disabled"
                  />
                </div>
                <div class="col">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="item.position"
                    :placeholder="
                      (question.fieldNameCargo || 'Rol') + ' ' + (index + 1)
                    "
                    :disabled="disabled"
                    required
                  />
                </div>
                <div class="col">
                  <input
                    type="date"
                    class="form-control form-control-sm"
                    v-model="item.period"
                    :disabled="disabled"
                    required
                  />
                </div>
              </div>
            </template>
          </ListEdit>
          <small v-if="showMinItemsWarning" class="text-danger d-block mt-1">
            {{ minItemsWarningText }}
          </small>
        </div>
      </b-collapse>

      <b-collapse
        :id="'subquestions-collapse-' + path.join('-')"
        :visible="showSubQuestionsCollapse"
      >
        <transition name="fade">
          <div class="sub-question-block">
            <StatementQuestion
              v-for="(subQuestion, subIndex) in question.childrenQuestionDTO"
              :key="subQuestion.id"
              :question="subQuestion"
              :modelValue="modelValue.subAnswers[subIndex]"
              :path="[...path, subIndex]"
              :disabled="disabled || !localIsTrue"
              @update:modelValue="handleSubQuestionUpdate"
            />
          </div>
        </transition>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import ListEdit from "@/components/shared/List-edit.vue";
import { formatRut, validateRut } from "@/plugins/complianceScripts/index.js";

export default {
  name: "StatementQuestion",

  components: {
    ListEdit,
  },

  props: {
    question: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: Object,
      required: true,
    },
    path: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localIsTrue:
        this.question.type === "SocietyList" ? true : this.modelValue.isTrue,
      showMinItemsWarning: false,
      minItemsWarningText: "",
      warningTimeoutId: null,
    };
  },

  watch: {
    "modelValue.isTrue"(newValue) {
      const effectiveValue =
        this.question.type === "SocietyList" ? true : newValue;
      if (effectiveValue !== this.localIsTrue) {
        this.localIsTrue = effectiveValue;
      }
    },
    localIsTrue: {
      handler(isTrueValue) {
        const newAnswer = {
          ...this.modelValue,
          isTrue: isTrueValue,
          value: "",
        };

        if (isTrueValue) {
          newAnswer.details = this.modelValue.details
            ? [...this.modelValue.details]
            : [];
          if (newAnswer.details.length === 0) {
            newAnswer.details.push({
              rut: "",
              name: "",
              position: "",
              period: "",
              __focused: false,
            });
          }
          newAnswer.subAnswers = this.modelValue.subAnswers
            ? [...this.modelValue.subAnswers]
            : this.initializeSubAnswers();
        } else {
          newAnswer.details = [];
          newAnswer.subAnswers = this.resetSubAnswers(
            this.modelValue.subAnswers || this.initializeSubAnswers()
          );
        }

        this.$emit("update:modelValue", { path: this.path, value: newAnswer });
      },
      immediate: true,
    },
  },

  computed: {
    questionNumber() {
      return this.path.map((p) => p + 1).join(".");
    },

    radioGroupName() {
      return `options-${this.path.join("-")}`;
    },

    hasSubQuestions() {
      return (
        this.question.childrenQuestionDTO &&
        this.question.childrenQuestionDTO.length > 0
      );
    },

    showSubQuestionsCollapse() {
      // Show sub-questions block if parent is "Sí" (localIsTrue) and sub-questions exist
      return this.localIsTrue && this.hasSubQuestions;
    },
  },
  methods: {
    initializeSubAnswers() {
      if (!this.question.childrenQuestionDTO) return [];
      return this.question.childrenQuestionDTO.map(() => ({
        isTrue: null,
        value: "",
        details: [],
        subAnswers: [],
      }));
    },

    resetSubAnswers(subAnswers) {
      if (!Array.isArray(subAnswers)) return [];
      return subAnswers.map((subAnswer) => ({
        ...subAnswer,
        isTrue: null,
        value: "",
        details: [],
      }));
    },

    handleAddDetail() {
      const updatedDetails = [...this.modelValue.details];
      updatedDetails.push({
        rut: "",
        name: "",
        position: "",
        period: "",
        __focused: false,
      });
      const updatedAnswer = { ...this.modelValue, details: updatedDetails };
      this.$emit("update:modelValue", {
        path: this.path,
        value: updatedAnswer,
      });
    },

    handleRemoveDetail(index) {
      if (this.modelValue.details && this.modelValue.details.length <= 1) {
        this.minItemsWarningText = "Se requiere al menos una sociedad";
        this.showMinItemsWarning = true;
        if (this.warningTimeoutId) {
          clearTimeout(this.warningTimeoutId);
        }
        this.warningTimeoutId = setTimeout(() => {
          this.showMinItemsWarning = false;
        }, 3000);
        return; // Prevent removal
      }

      const updatedDetails = [...this.modelValue.details];
      updatedDetails.splice(index, 1);
      const updatedAnswer = { ...this.modelValue, details: updatedDetails };
      this.$emit("update:modelValue", {
        path: this.path,
        value: updatedAnswer,
      });
    },

    handleSubQuestionUpdate(event) {
      this.$emit("update:modelValue", event);
    },

    validateRutMethod(event) {
      return validateRut(event);
    },

    handleInputRUT(event, item) {
      this.$set(item, "rut", event.target.value);
    },

    handleFocusRUT(item) {
      this.$set(item, "__focused", true);
    },

    handleBlurRUT(item) {
      this.$set(item, "__focused", false);
    },

    rutDisplayValue(item) {
      const value = item.rut;
      if (item.__focused) {
        return value;
      }
      return value ? formatRut(value) : "";
    },
  },
};
</script>

<style scoped>
.sub-question-block {
  margin-top: 0.75rem;
  margin-left: 2rem;
  padding: 0.75rem;
  background-color: #f8f9faa8;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
}

.sub-question-block > div:last-child {
  border-bottom-width: 0 !important;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}
</style>
